// Installing Лупа as an app (the PWA): icon on the home screen, its own window, and — on iPhone —
// the only way to get push at all. The site already is installable (vite.config.ts → manifest +
// service worker); this module only decides whether and how to offer it.
//
//   - Chrome / Edge / Samsung / Yandex on Android and desktop announce installability with
//     `beforeinstallprompt`. We keep that event and show our own «Установить» button; calling
//     preventDefault also keeps Chrome's own mini-infobar off pages where it does not belong
//     (a customer's tracking link, a driver who should take the Android app instead).
//   - iOS has no such event and no API: installing is «Поделиться» → «На экран „Домой“», which we
//     can only explain. In-app browsers (Telegram, VK, mail) cannot do it at all.
//   - Already running as the installed app → nothing to offer.

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** What the page can offer: our button, the iOS steps, «open in Safari first», or nothing. */
export type InstallHint = 'prompt' | 'ios' | 'ios-open-in-safari' | null

export type PlatformInfo = {
  userAgent: string
  /** Touch events exist — tells an iPad (which reports a Mac UA since iPadOS 13) from a Mac. */
  touch: boolean
  /** Already running as the installed app. */
  standalone: boolean
}

export function isIOSUserAgent(userAgent: string, touch: boolean): boolean {
  return /iPad|iPhone|iPod/.test(userAgent) || (userAgent.includes('Macintosh') && touch)
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  return isIOSUserAgent(navigator.userAgent, typeof document !== 'undefined' && 'ontouchend' in document)
}

/** Whether the page runs as the installed app (home-screen icon, own window). */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false
  const displayStandalone = window.matchMedia?.('(display-mode: standalone)').matches ?? false
  // iOS Safari exposes the legacy navigator.standalone flag instead of display-mode.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  return displayStandalone || iosStandalone
}

export function installHint(platform: PlatformInfo, canPrompt: boolean): InstallHint {
  if (platform.standalone) return null
  if (canPrompt) return 'prompt'
  if (isIOSUserAgent(platform.userAgent, platform.touch)) {
    // Safari and the other real iOS browsers (Chrome «CriOS», Firefox «FxiOS», Edge, Yandex) carry
    // the «Safari/» token and, since iOS 16.4, all have «На экран „Домой“». An in-app browser is a
    // bare WKWebView without it, and its share menu has no such item.
    return /Safari\//.test(platform.userAgent) ? 'ios' : 'ios-open-in-safari'
  }
  return null
}

// --- wording, the same on the site and the phone site -----------------------------------------

export const PWA_INSTALL_TITLE = 'Приложение Лупа'

/** The line under the title. `installed` wins: the app was just installed from our button. */
export function pwaInstallText(hint: InstallHint, installed: boolean): string {
  if (installed) return 'Готово — Лупа установлена. Открывайте её с иконки на главном экране или рабочем столе.'
  switch (hint) {
    case 'prompt':
      return 'Добавьте Лупу на рабочий стол для быстрого доступа и получения уведомлений о заявках.'
    case 'ios':
      return 'Иконка на экране «Домой» и своё окно без адресной строки. Уведомления на iPhone и iPad работают только так.'
    case 'ios-open-in-safari':
      return 'Сайт открыт внутри другого приложения. Откройте lupaapp.ru в Safari — добавить на экран «Домой» можно только оттуда.'
    default:
      return ''
  }
}

/** iOS steps. `icon` names the symbol Safari shows, so the card can draw the same one. */
export const IOS_INSTALL_STEPS: ReadonlyArray<{ icon: 'share' | 'add' | 'open'; text: string }> = [
  { icon: 'share', text: 'Нажмите «Поделиться» в Safari. В новых iOS она в меню «⋯».' },
  { icon: 'add', text: 'Выберите «На экран „Домой“» — если пункта не видно, прокрутите список ниже.' },
  { icon: 'open', text: 'Нажмите «Добавить» и открывайте Лупу с новой иконки.' },
]

// --- the browser's install event, kept from page load on -------------------------------------
// `beforeinstallprompt` fires once, early, whatever page is open — so it is caught at start-up
// (main.tsx), not by the card that may be mounted much later.

let deferredPrompt: BeforeInstallPromptEvent | null = null
let installedThisVisit = false
let version = 0
let capturing = false
const listeners = new Set<() => void>()

function changed() {
  version += 1
  listeners.forEach((listener) => listener())
}

export function startPwaInstallCapture(): void {
  if (capturing || typeof window === 'undefined') return
  capturing = true
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    changed()
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    installedThisVisit = true
    changed()
  })
}

export function subscribePwaInstall(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function pwaInstallVersion(): number {
  return version
}

export function currentInstallHint(): InstallHint {
  if (installedThisVisit) return null
  return installHint(
    {
      userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
      touch: typeof document !== 'undefined' && 'ontouchend' in document,
      standalone: isStandaloneDisplay(),
    },
    deferredPrompt !== null,
  )
}

/** Installed from our button during this visit: the card says where to find the app. */
export function wasInstalledThisVisit(): boolean {
  return installedThisVisit
}

/**
 * Shows the browser's install dialog. The event can be used only once, so the button goes away
 * whatever the answer; the browser offers it again on a later visit if the person said no.
 */
export async function promptPwaInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferredPrompt
  if (!event) return 'unavailable'
  deferredPrompt = null
  changed()
  try {
    await event.prompt()
    const { outcome } = await event.userChoice
    return outcome
  } catch {
    return 'unavailable'
  }
}

/** Test seam: forget the captured event between tests (the window listeners stay). */
export function resetPwaInstallForTests(): void {
  deferredPrompt = null
  installedThisVisit = false
  changed()
}
